import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { MAX_WIDTH } from '../../constants/platform';
import MyAccountMenu from '../../components/MyAccountMenu/MyAccountMenu';
import PageTitle from '../../components/PageTitle';
import { COLORS, FONTS } from '../../constants';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { Footer } from '../../components/Footer';
import { ActivitiesBookDetailScreenProps } from './types';
import { IActivity } from '../../types/activities.types';
import ActivityApi from '../../services/api/activity.api';
import { Loading } from '../../components';
import dayjs from 'dayjs';
import { useRole } from '../../hooks/useRole';
import { Button } from '../../components/Button';
import { BookActivitySuccess } from './components/BookActivitySuccess';
import { useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { isSameDate } from '../../services/utils/dateConvert';
import RenderHTML from 'react-native-render-html';
import { Alert } from '../../services/alert';
import { DATE_TYPE } from '../../constants/activity';
import { convertToAmPm } from '../../services/utils';

const ActivitiesBookDetail: FC<ActivitiesBookDetailScreenProps> = ({
  route,
  navigation,
}) => {
  const { isVolunteer } = useRole();
  const activityId = route.params.id;
  const [activity, setActivity] = useState<IActivity>();
  const [bookingDescription, setBookingDescription] = useState<string>('');
  const [mode, setMode] = useState<'view' | 'success'>('view');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingSignup, setIsLoadingSignup] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const { user } = useSelector((redux: RootState) => redux.authReducer);
  const scrollViewRef = useRef<ScrollView>(null);

  const moveScreenTop = () => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        y: 0,
      });
    }
  };

  const getActivity = useCallback(async () => {
    setIsLoading(true);
    ActivityApi.getActivity(activityId)
      .then((res) => {
        setActivity(res.data.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activityId]);

  const signUpActivity = useCallback(async () => {
    setIsLoadingSignup(true);
    ActivityApi.postSignupActivity(activityId)
      .then((res) => {
        moveScreenTop();
        setMode('success');
      })
      .catch((error) => {
        Alert.alert('Error', error.meta.message);
        console.log(error);
      })
      .finally(() => {
        setIsLoadingSignup(false);
      });
  }, [activityId]);

  const fetchBookingSetting = useCallback(async () => {
    ActivityApi.getBookingSetting()
      .then((res) => {
        setBookingDescription(res.data.data.booking_description);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const onPressSignup = useCallback(async () => {
    Alert.alert(`Register as an ${user?.type_name} for this activity?`, '', [
      {
        text: 'Cancel',
        onPress: () => {},
      },
      {
        text: 'Confirm',
        onPress: () => {
          signUpActivity();
        },
      },
    ]);
  }, [signUpActivity, user?.type_name]);

  const pageTitle = useMemo(() => {
    return isVolunteer
      ? 'Volunteer For This Activity'
      : 'Instruct This Activity';
  }, [isVolunteer]);

  useEffect(() => {
    getActivity();
  }, [getActivity]);

  useEffect(() => {
    fetchBookingSetting();
  }, [fetchBookingSetting]);

  if (isDesktop()) {
    return (
      <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
        <View
          style={{
            maxWidth: MAX_WIDTH,
            width: '90%',
            marginHorizontal: 'auto',
            paddingTop: 40,
            marginBottom: 200,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <View style={{ width: '22%', minWidth: 230, marginRight: 20 }}>
              <MyAccountMenu />
            </View>
            <View style={{ width: '70%' }}>
              {mode === 'view' && (
                <View>
                  <PageTitle
                    textStyle={{ textTransform: 'uppercase' }}
                    title={pageTitle}
                    haveGoBackButton={true}
                  />
                  <View
                    style={{
                      paddingHorizontal: 40,
                      paddingTop: 40,
                      backgroundColor: COLORS.LIGHT_RED,
                      borderRadius: 18,
                      flexDirection: 'column',
                      shadowColor: '#F0F0F0',
                      shadowOffset: { width: 0, height: 12 },
                      shadowOpacity: 1,
                      shadowRadius: 32,
                      paddingBottom: 35,
                      elevation: 20,
                    }}
                  >
                    {isLoading ? (
                      <Loading />
                    ) : activity ? (
                      <View>
                        <Image
                          source={
                            activity.image
                              ? {
                                  uri: activity.image,
                                }
                              : require('../../assets/activities-image.png')
                          }
                          style={{
                            width: '100%',
                            height: 400,
                            borderRadius: 18,
                            marginBottom: 20,
                          }}
                        />
                        <View
                          style={[{ flexDirection: 'row', marginBottom: 20 }]}
                        >
                          <View style={{ width: '20%' }}>
                            <Text
                              style={{
                                fontFamily: FONTS.AvenirMedium,
                                fontWeight: '800',
                                fontSize: 19,
                                letterSpacing: 1,
                                marginBottom: 10,
                                color: COLORS.BLACK,
                              }}
                            >
                              {activity?.name}
                            </Text>
                            <View
                              style={{
                                width: 58,
                                height: 2,
                                backgroundColor: COLORS.RED_PRIMARY,
                                marginBottom: 10,
                              }}
                            />
                            <Text style={styles.desktopSubText}>
                              {activity.type}
                            </Text>
                            <Text style={styles.desktopSubText}>
                              {activity.venue}
                            </Text>

                            {activity.date_type.value === DATE_TYPE.NORMAL ? (
                              activity.start_date === activity.end_date ? (
                                <Text style={styles.desktopSubText}>
                                  {`${dayjs(activity.start_date).format(
                                    'MMMM DD, YYYY'
                                  )}`}
                                </Text>
                              ) : (
                                <>
                                  <Text style={styles.desktopSubText}>
                                    {`Start: ${dayjs(
                                      activity.start_date
                                    ).format('MMMM DD, YYYY')}`}
                                  </Text>
                                  <Text style={styles.desktopSubText}>
                                    {`End: ${dayjs(activity.end_date).format(
                                      'MMMM DD, YYYY'
                                    )}`}
                                  </Text>
                                </>
                              )
                            ) : (
                              <Text style={styles.desktopSubText}>
                                {activity.date_type.name}
                              </Text>
                            )}

                            <View
                              style={{
                                borderRadius: 18,
                                paddingVertical: 2,
                                paddingHorizontal: 15,
                                backgroundColor: COLORS.LIGHT_GREY,
                                width: 150,
                              }}
                            >
                              <Text
                                style={{
                                  fontFamily: FONTS.AvenirMedium,
                                  fontSize: 12,
                                  fontWeight: '500',
                                  color: COLORS.BLACK,
                                  textAlign: 'center',
                                  lineHeight: 18,
                                }}
                              >
                                {`${convertToAmPm(
                                  activity.start_time
                                )} - ${convertToAmPm(activity.end_time)}`}
                              </Text>
                            </View>
                          </View>
                          <View style={{ width: '80%' }}>
                            <RenderHTML
                              source={{ html: activity.description }}
                              tagsStyles={{
                                body: {
                                  fontSize: 16,
                                  fontFamily: FONTS.AvenirRegular,
                                  color: COLORS.BLACK,
                                  fontWeight: '500',
                                  letterSpacing: 1,
                                },
                                p: {
                                  marginTop: 0,
                                  marginBottom: 10,
                                },
                              }}
                            />
                            {/* <Text
                              style={{
                                fontFamily: FONTS.AvenirRegular,
                                fontSize: 18,
                                color: COLORS.BLACK,
                                fontWeight: '500',
                              }}
                            >
                              {activity.description}
                            </Text> */}
                          </View>
                        </View>
                        <View>
                          <View style={{ marginBottom: 40 }}>
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginBottom: 4,
                              }}
                            >
                              <Image
                                source={require('../../assets/warning-information-icon.png')}
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginRight: 7,
                                }}
                              />
                              <Text
                                style={{
                                  fontWeight: '700',
                                  fontFamily: FONTS.AvenirMedium,
                                  fontSize: 19,
                                  color: COLORS.BLACK,
                                  letterSpacing: 1,
                                }}
                              >
                                {`${user?.type_name} Booking Note:`}
                              </Text>
                            </View>
                            <Text
                              style={{
                                fontFamily: FONTS.AvenirMedium,
                                fontWeight: '500',
                                fontSize: 16,
                                color: COLORS.GREY,
                              }}
                            >
                              {bookingDescription}
                            </Text>
                          </View>
                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'center',
                            }}
                          >
                            <Button
                              text="BOOK NOW"
                              onPress={onPressSignup}
                              arrowRight={false}
                              textStyle={[
                                {
                                  color: COLORS.WHITE,
                                },
                                styles.buttonTextStyle,
                              ]}
                              style={styles.buttonStyle}
                              desktopButtonStyle={{
                                width: 350,
                                borderRadius: 14,
                              }}
                            />
                          </View>
                        </View>
                      </View>
                    ) : (
                      <></>
                    )}
                  </View>
                </View>
              )}
              {mode === 'success' && activity && (
                <BookActivitySuccess activity={activity} />
              )}
            </View>
          </View>
        </View>
        {isLoadingSignup && <Loading isFullScreen />}
        <Footer />
      </ScrollView>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
      {mode === 'view' && (
        <View>
          <PageTitle
            textStyle={{ textTransform: 'uppercase', textAlign: 'center' }}
            title={pageTitle}
            haveGoBackButton={true}
          />
          <View style={{ minHeight: 100 }}>
            {isLoading ? (
              <Loading />
            ) : activity ? (
              <View>
                <Image
                  source={
                    activity.image
                      ? {
                          uri: activity.image,
                        }
                      : require('../../assets/activities-image.png')
                  }
                  style={{
                    width: '100%',
                    height: 220,
                  }}
                />
                <View
                  style={{
                    paddingHorizontal: 20,
                    paddingTop: 30,
                    backgroundColor: COLORS.WHITE,
                    marginBottom: 20,
                  }}
                >
                  <View
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 20,
                      backgroundColor: '#F9F1F1',
                      borderRadius: 18,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'column',
                        gap: 15,
                        marginBottom: 15,
                      }}
                    >
                      <View
                        style={{
                          flex: 1,
                        }}
                      >
                        <Text
                          style={{
                            fontFamily: FONTS.AvenirMedium,
                            fontWeight: '800',
                            fontSize: 19,
                            letterSpacing: 1,
                            marginBottom: 10,
                            color: COLORS.BLACK,
                            textTransform: 'uppercase',
                          }}
                        >
                          {activity.name}
                        </Text>
                        <View
                          style={{
                            width: 120,
                            height: 2,
                            backgroundColor: COLORS.RED_PRIMARY,
                            marginBottom: 10,
                          }}
                        />
                        <Text style={styles.textMobile}>{activity.type}</Text>
                        <Text style={styles.textMobile}>{activity.venue}</Text>
                        {activity.date_type.value === DATE_TYPE.NORMAL ? (
                          activity.start_date === activity.end_date ? (
                            <Text style={styles.textMobile}>
                              {`${dayjs(activity.start_date).format(
                                'MMMM DD, YYYY'
                              )}`}
                            </Text>
                          ) : (
                            <>
                              <Text style={styles.textMobile}>
                                {`Start: ${dayjs(activity.start_date).format(
                                  'MMMM DD, YYYY'
                                )}`}
                              </Text>
                              <Text style={styles.textMobile}>
                                {`End: ${dayjs(activity.end_date).format(
                                  'MMMM DD, YYYY'
                                )}`}
                              </Text>
                            </>
                          )
                        ) : (
                          <Text style={styles.textMobile}>
                            {activity.date_type.name}
                          </Text>
                        )}
                        <View
                          style={{
                            borderRadius: 18,
                            paddingVertical: 2,
                            paddingHorizontal: 15,
                            backgroundColor: COLORS.LIGHT_GREY,
                            width: 150,
                          }}
                        >
                          <Text
                            style={{
                              fontFamily: FONTS.AvenirMedium,
                              fontSize: 12,
                              fontWeight: '500',
                              color: COLORS.BLACK,
                              textAlign: 'center',
                              lineHeight: 18,
                            }}
                          >
                            {`${convertToAmPm(
                              activity.start_time
                            )} - ${convertToAmPm(activity.end_time)}`}
                          </Text>
                        </View>
                      </View>
                    </View>
                    <View style={{ marginBottom: 20 }}>
                      <RenderHTML
                        source={{ html: activity.description }}
                        tagsStyles={{
                          body: {
                            fontSize: 16,
                            fontFamily: FONTS.AvenirRegular,
                            color: COLORS.BLACK,
                            fontWeight: '500',
                            letterSpacing: 1,
                          },
                          p: {
                            marginTop: 0,
                            marginBottom: 10,
                          },
                        }}
                      />
                    </View>
                    <View>
                      <View style={{ marginBottom: 25 }}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: 10,
                          }}
                        >
                          <Image
                            source={require('../../assets/warning-information-icon.png')}
                            style={{
                              width: 20,
                              height: 20,
                              marginRight: 7,
                            }}
                          />
                          <Text
                            style={{
                              fontWeight: '700',
                              fontFamily: FONTS.AvenirMedium,
                              fontSize: 19,
                              color: COLORS.BLACK,
                              letterSpacing: 1,
                            }}
                          >
                            {`${user?.type_name} Booking Note:`}
                          </Text>
                        </View>
                        <Text style={styles.textMobile}>
                          {bookingDescription}
                        </Text>
                      </View>
                      <View style={{}}>
                        <Button
                          onPress={onPressSignup}
                          text="BOOK NOW"
                          arrowRight={false}
                          textStyle={[
                            {
                              color: COLORS.WHITE,
                            },
                            styles.buttonTextStyle,
                          ]}
                          style={styles.buttonMobileStyle}
                        />
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            ) : (
              <></>
            )}
          </View>
        </View>
      )}
      {mode === 'success' && activity && (
        <View
          style={{
            paddingHorizontal: 20,
            paddingTop: 20,
            marginBottom: 30,
          }}
        >
          <BookActivitySuccess activity={activity} />
        </View>
      )}
      {isLoadingSignup && <Loading isFullScreen />}
      <Footer />
    </ScrollView>
  );
};

export default ActivitiesBookDetail;

const styles = StyleSheet.create({
  campTitle: {
    fontFamily: FONTS.Arial,
    fontWeight: '700',
    fontSize: 20,
    letterSpacing: 1.7,
    color: COLORS.BLACK,
    textTransform: 'uppercase',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.GREY,
    paddingBottom: 15,
    marginBottom: 20,
  },
  desktopSubText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 14,
    color: COLORS.GREY,
    letterSpacing: 1,
    marginBottom: 10,
  },
  text: {
    fontFamily: FONTS.AvenirRegular,
    fontSize: 16,
    color: COLORS.BLACK,
    fontWeight: '500',
    letterSpacing: 1,
    marginBottom: 10,
  },
  textMobile: {
    fontSize: 15,
    fontFamily: FONTS.AvenirRegular,
    color: COLORS.GREY,
    fontWeight: '500',
    letterSpacing: 1,
    marginBottom: 10,
  },
  buttonStyle: {
    paddingVertical: 17,
    borderRadius: 14,
  },
  buttonMobileStyle: {
    paddingVertical: 15,
    borderRadius: 14,
    width: '100%',
  },
  buttonTextStyle: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 19,
    fontWeight: '800',
    letterSpacing: 1,
  },
});
